import { KnowledgeAskCitation } from '@digital-twins/shared-types'

import i18n from '@/i18n'

import { isWebAssistantCitation } from './partitionAssistantCitations'

export interface IndexedCitation {
  index: number
  citation: KnowledgeAskCitation
}

export interface CitationDocumentGroup {
  key: string
  citations: Array<IndexedCitation>
}

/** Years at or beyond this age show the "fairly old" notice on a source card. */
export const SOURCE_OLD_YEAR_THRESHOLD = 5

export function citationDocumentKey(citation: KnowledgeAskCitation): string {
  return citation.documentToken || citation.documentName || 'source'
}

/**
 * Collapse citations into one card per document, keeping 1-based indexes so
 * they match the inline chips in the answer.
 */
export function groupCitationsByDocument(
  citations: Array<KnowledgeAskCitation>,
): Array<CitationDocumentGroup> {
  // Defensive only: a `kind: 'web'` citation should never reach this
  // function once a caller runs `partitionAssistantCitations` first (the
  // single shared seam — see partitionAssistantCitations.ts). Without this,
  // `citationDocumentKey` falls back to 'source' for every one of them and
  // they all collapse into one group with no real document behind it.
  const documentOnly = citations.filter(
    (citation) => !isWebAssistantCitation(citation),
  )

  const groups: Array<CitationDocumentGroup> = []
  const indexByKey = new Map<string, number>()

  documentOnly.forEach((citation, i) => {
    const key = citationDocumentKey(citation)
    const item: IndexedCitation = { index: i + 1, citation }
    const existing = indexByKey.get(key)
    if (existing == null) {
      indexByKey.set(key, groups.length)
      groups.push({ key, citations: [item] })
      return
    }
    groups[existing].citations.push(item)
  })

  return groups
}

export function referencesSidebarTitle(sourceCount: number): string {
  return i18n.t('threads.detail.basedOnSources', {
    count: sourceCount,
    ns: 'app',
  })
}

export function isSourceFairlyOld(
  yearValue: string | null | undefined,
  nowYear: number = new Date().getFullYear(),
): boolean {
  if (!yearValue) {
    return false
  }
  const year = Number.parseInt(yearValue, 10)
  if (!Number.isFinite(year)) {
    return false
  }
  return nowYear - year >= SOURCE_OLD_YEAR_THRESHOLD
}
